export const DEFAULT_UI_LOCALE = 'zh-CN';
export const DEFAULT_REPORT_LOCALE = 'en-US';

export const SUPPORTED_LOCALES = Object.freeze([
  { code: 'en-US', nativeName: 'English', direction: 'ltr' },
  { code: 'ar-SA', nativeName: 'العربية', direction: 'rtl' },
  { code: 'zh-CN', nativeName: '简体中文', direction: 'ltr' },
  { code: 'zh-HK', nativeName: '繁體中文（香港）', direction: 'ltr' },
  { code: 'zh-TW', nativeName: '繁體中文（台灣）', direction: 'ltr' },
  { code: 'zh-MO', nativeName: '繁體中文（澳門）', direction: 'ltr' },
  { code: 'vi-VN', nativeName: 'Tiếng Việt', direction: 'ltr' },
  { code: 'th-TH', nativeName: 'ไทย', direction: 'ltr' },
  { code: 'id-ID', nativeName: 'Bahasa Indonesia', direction: 'ltr' },
  { code: 'ms-MY', nativeName: 'Bahasa Melayu', direction: 'ltr' },
  { code: 'ru-RU', nativeName: 'Русский', direction: 'ltr' },
]);

const SUPPORTED_LOCALE_LOOKUP = new Map(
  SUPPORTED_LOCALES.map((locale) => [locale.code.toLowerCase(), locale.code])
);

const LEGACY_LOCALES = Object.freeze({
  en: 'en-US',
  zh: 'zh-CN',
  ar: 'ar-SA',
  vi: 'vi-VN',
  th: 'th-TH',
  id: 'id-ID',
  ms: 'ms-MY',
  ru: 'ru-RU',
});

const cleanLocale = (value) => String(value || '').trim().replace(/_/g, '-');

export const normalizeUiLocale = (value, fallback = DEFAULT_UI_LOCALE) => {
  const cleaned = cleanLocale(value);
  if (!cleaned) {
    return fallback;
  }

  const exact = SUPPORTED_LOCALE_LOOKUP.get(cleaned.toLowerCase());
  if (exact) {
    return exact;
  }

  const segments = cleaned.toLowerCase().split('-');
  const language = segments[0];
  if (language === 'zh') {
    if (segments.includes('hk')) return 'zh-HK';
    if (segments.includes('tw')) return 'zh-TW';
    if (segments.includes('mo')) return 'zh-MO';
    return 'zh-CN';
  }
  return LEGACY_LOCALES[language] || fallback;
};

// Report jobs historically persisted short codes. Keep those readable without
// applying the new-visit Arabic-to-English mapping in the browser.
export const normalizeReportLocale = (value, fallback = DEFAULT_REPORT_LOCALE) => {
  const cleaned = cleanLocale(value);
  if (!cleaned) {
    return fallback;
  }
  if (cleaned.toLowerCase() === 'zh') {
    return 'zh-CN';
  }
  if (cleaned.toLowerCase() === 'ar') {
    return 'ar-SA';
  }
  if (cleaned.toLowerCase() === 'en') {
    return 'en-US';
  }
  return normalizeUiLocale(cleaned, fallback);
};

export const isChineseLocale = (value) => normalizeUiLocale(value).startsWith('zh-');
export const isRtlLocale = (value) => normalizeUiLocale(value) === 'ar-SA';
export const getLocaleDirection = (value) => (isRtlLocale(value) ? 'rtl' : 'ltr');

export const getReportLocaleDirection = (value) => (
  normalizeReportLocale(value) === 'ar-SA' ? 'rtl' : 'ltr'
);

export const resolveReportLocale = (report, visit) => {
  const persistedLanguage = report?.report_language
    || report?.generation_language
    || report?.language;
  if (persistedLanguage) {
    return normalizeReportLocale(persistedLanguage);
  }

  const visitLocale = normalizeUiLocale(
    visit?.language || report?.visit_language,
    DEFAULT_REPORT_LOCALE
  );
  if (Number(report?.report_flow_version) === 2 && visitLocale === 'ar-SA') {
    return DEFAULT_REPORT_LOCALE;
  }
  return normalizeReportLocale(visitLocale);
};

const readNamePart = (patient, primaryKey, responseKey) => String(
  patient?.[primaryKey] ?? patient?.[responseKey] ?? ''
).trim();

const formatPatientNameForLocale = (patient, normalizedLocale, fallbackName = '') => {
  const firstName = readNamePart(patient, 'first_name', 'patient_first_name');
  const lastName = readNamePart(patient, 'last_name', 'patient_last_name');

  if (!firstName && !lastName) {
    return String(fallbackName || patient?.patient_name || '').trim();
  }

  if (normalizedLocale.startsWith('zh-')) {
    return `${lastName}${firstName}`;
  }
  if (normalizedLocale === 'vi-VN') {
    return [lastName, firstName].filter(Boolean).join(' ');
  }
  return [firstName, lastName].filter(Boolean).join(' ');
};

export const formatPatientName = (patient, locale, fallbackName = '') => (
  formatPatientNameForLocale(patient, normalizeUiLocale(locale), fallbackName)
);

export const formatReportPatientName = (patient, locale, fallbackName = '') => (
  formatPatientNameForLocale(patient, normalizeReportLocale(locale), fallbackName)
);

export const formatDateTime = (value, locale, options) => {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(
    normalizeUiLocale(locale),
    options || { dateStyle: 'medium', timeStyle: 'medium' }
  ).format(date);
};

export const formatDate = (value, locale, options) => formatDateTime(
  value,
  locale,
  options || { year: 'numeric', month: 'numeric', day: 'numeric' }
);

export const formatReportDateTime = (value, locale, options) => {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(
    normalizeReportLocale(locale),
    options || { dateStyle: 'medium', timeStyle: 'medium' }
  ).format(date);
};

export const createNumberFormatter = (locale, options) => (
  new Intl.NumberFormat(normalizeUiLocale(locale), options)
);
